/**
 * Loads additional posts and keeps the load-more UI in sync.
 *
 * @package AGDS
 * @category Modules
 * @module PostLoader
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class PostsLoader {
    /**
     * Creates a posts loader instance.
     *
     * @param {Object} options
     * @param {ApiService} options.api
     * @param {HTMLElement} [options.preloader]
     * @param {HTMLElement} options.postsContainer
     */
    constructor(options = {}) {
        this.preloader = options.preloader;
        this.api = options.api;
        this.spollers = options.spollers;
        this.postsContainer = options.postsContainer;

        this.button = this.postsContainer?.parentElement.querySelector(".loadmore-btn");

        this.isLoading = false;
        this.scrollHandler = this.handleScroll.bind(this);
        this.clickHandler = this.loadPosts.bind(this);
    }

    /**
     * Initializes the loader.
     *
     * @returns {void}
     */
    init() {
        if (!this.postsContainer || !this.button) {
            return;
        }

        this.currentPage = parseInt(this.button.dataset.currentPage, 10) || 1;
        this.postType = this.button.dataset.postType;
        this.sendingText = this.button.dataset.btnSending;
        this.originalText = this.button.textContent;

        this.button.addEventListener("click", this.clickHandler);
        // window.addEventListener("scroll", this.scrollHandler, { passive: true });
    }

    /**
     * Loads the next posts page.
     *
     * @async
     * @returns {void}
     */
    async loadPosts() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.toggleLoadingUI(true);

        try {
            let response;

            const hasFilters =
                this.filters &&
                ((this.filters.categories && this.filters.categories.length > 0) ||
                    (this.filters.tags && this.filters.tags.length > 0) ||
                    (this.filters.tax_query && this.filters.tax_query.length > 0));

            const pageToLoad = this.currentPage + 1;
            const archiveTermId = parseInt(this.button.dataset.termId, 10) || null;

            if (hasFilters) {
                response = await this.api.postsFilter(this.postType, pageToLoad, this.filters);
            } else {
                if (archiveTermId) {
                    response = await this.api.postsLoadMore(this.postType, pageToLoad, { cat: archiveTermId });
                } else {
                    response = await this.api.postsLoadMore(this.postType, pageToLoad);
                }
            }

            if (response.data.html) {
                this.postsContainer.insertAdjacentHTML("beforeend", response.data.html);

                await this.waitForImages(this.postsContainer);

                this.currentPage = pageToLoad;
                this.button.dataset.page = this.currentPage;
            }

            if (!response.data.has_more) {
                this.hideButton();
                this.destroyScroll();
            }

            if (this.spollers) {
                this.spollers.init();
            }
        } catch (err) {
            console.error("Error loadPosts:", err);
            this.hideButton();
            this.destroyScroll();
        } finally {
            this.isLoading = false;
            this.toggleLoadingUI(false);
        }
    }

    /**
     * Resets loader state after filtering.
     *
     * @param {Object} newFilters - New filter parameters.
     * @param {boolean} hasMore - Whether more posts are available.
     * @returns {void}
     */
    reset(newFilters = {}, hasMore = true) {
        this.filters = newFilters;
        this.currentPage = 1;

        this.button = this.postsContainer?.parentElement.querySelector(".loadmore-btn");
        if (!this.button) return;

        this.button.dataset.page = "1";
        this.button.dataset.currentPage = "1";
        this.button.querySelector("span").innerHTML = this.originalText;
        this.button.disabled = false;

        if (hasMore) {
            this.button.parentElement.style.display = "";
            this.button.style.display = "";

            window.removeEventListener("scroll", this.scrollHandler);
            window.addEventListener("scroll", this.scrollHandler, { passive: true });
        } else {
            this.hideButton();
            this.destroyScroll();
        }
    }

    /**
     * Loads posts when the button enters the viewport.
     *
     * @returns {void}
     */
    handleScroll() {
        if (!this.button || this.isLoading) return;

        const rect = this.button.getBoundingClientRect();
        const isVisible =
            rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 100;

        if (isVisible) {
            this.loadPosts();
        }
    }

    /**
     * Toggles loading state on the button and preloader.
     *
     * @param {boolean} isLoading - Whether posts are loading.
     * @returns {void}
     */
    toggleLoadingUI(isLoading) {
        if (isLoading) {
            this.button.querySelector("span").innerHTML = this.sendingText;
            this.button.disabled = true;
            this.preloader?.classList.add("_show");
        } else {
            this.button.querySelector("span").innerHTML = this.originalText;
            this.button.disabled = false;
            this.preloader?.classList.remove("_show");
        }
    }

    /**
     * Hides the load-more button.
     *
     * @returns {void}
     */
    hideButton() {
        this.button.parentElement.style.display = "none";
    }

    /**
     * Waits until newly inserted images finish loading.
     *
     * @param {HTMLElement} container - Container with newly inserted posts.
     * @returns {Promise<void[]>}
     */
    async waitForImages(container) {
        const newImages = Array.from(container.querySelectorAll('img:not([data-loaded="true"])'));

        const imagesToLoad = newImages.filter((img) => !img.complete);

        newImages.forEach((img) => (img.dataset.loaded = "true"));

        if (imagesToLoad.length === 0) {
            return Promise.resolve();
        }

        const promises = imagesToLoad.map(
            (img) =>
                new Promise((resolve, reject) => {
                    img.addEventListener("load", resolve, { once: true });
                    img.addEventListener("error", reject, { once: true });
                }),
        );

        return Promise.all(promises);
    }

    /**
     * Removes the scroll listener.
     *
     * @returns {void}
     */
    destroyScroll() {
        window.removeEventListener("scroll", this.scrollHandler);
    }

    /**
     * Removes event listeners and DOM references.
     *
     * @returns {void}
     */
    destroy() {
        this.destroyScroll();
        this.button?.removeEventListener("click", this.clickHandler);

        this.postsContainer = null;
        this.preloader = null;
        this.button = null;
    }
}
